// Financial Reporting tab — the header above the selected type's requests.
//
// It answers the question the tab is opened with: what is owed next, when, and
// can I ask for it. The period and the due date are the SERVER's — read from
// the issue-options endpoint, which is the same computation `IssueAsync`
// re-runs before it accepts anything, so this header can never advertise a
// deadline the endpoint would compute differently.
//
// 🔴 A TYPE WITH A PRECONDITION STATES IT AND CARRIES ITS DOOR. All four chips
// always render, so a reader who selects Annual on a loan with no tax date sees
// why Annual cannot be issued and the control that fixes it, instead of a grey
// button and no explanation. The doors are rendered only to a viewer who can
// walk through them — "Record date" needs the reviewer permission the panel
// itself needs, "Put on watch" needs the financial-ops permission — because a
// door that 403s is worse than no door.
//
// 🔴 THE BLOCK AND THE ADVISORY ARE DIFFERENT THINGS, AND ARE DRAWN
// DIFFERENTLY. Only Annual is refused by the server (`ResolveDueDate` returns a
// blocked reason exactly for Annual-with-no-tax-date, and `IssueAsync` throws
// on it). DSCR-Watch off watch and Default/Workout with no open case are NOT
// server blocks — the issue path for a Default/Workout request carries the
// comment "no open case is a normal state, and it must not block issuing" and
// links the case best-effort. So those two state their consequence and keep the
// action live. Hard-disabling them here would withhold something the endpoint
// allows, which is the same defect as offering something it refuses, pointing
// the other way.

import { AlertTriangle, CalendarClock } from "lucide-react";
import { Button, Card, Skeleton, StatusBadge } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import {
  REPORT_REQUEST_TYPE_LABEL,
  type DscrWatchStateDto,
  type ReportRequestDto,
  type ReportRequestType,
} from "@/api/portfolioAdmin/reportRequestsApi";
import { useReportRequestIssueOptions } from "@/hooks/portfolioAdmin/useReportRequests";
import { requestForPeriod } from "./reportRequestSummary";

interface ReportRequestTypeHeaderProps {
  loanId: string;
  type: ReportRequestType;
  /** Every request on the loan — narrowed to this type here. */
  requests: readonly ReportRequestDto[];
  /** Reporting.ReviewRequests. */
  canReview: boolean;
  /** Compliance.RecordFinancialOpsCheck — gates the DSCR-Watch door only. */
  canToggleWatch: boolean;
  watch: DscrWatchStateDto;
  /** Whether the loan has an open default case, from loan health. */
  hasOpenDefaultCase: boolean;
  onIssue: (type: ReportRequestType) => void;
  onOpenRequest: (request: ReportRequestDto) => void;
  /** Door for the Annual block — focuses the tax-completion field. */
  onRecordTaxDate: () => void;
  /** Door for the DSCR-Watch precondition — opens the watch toggle. */
  onPutOnWatch: () => void;
}

export function ReportRequestTypeHeader({
  loanId,
  type,
  requests,
  canReview,
  canToggleWatch,
  watch,
  hasOpenDefaultCase,
  onIssue,
  onOpenRequest,
  onRecordTaxDate,
  onPutOnWatch,
}: ReportRequestTypeHeaderProps) {
  const options = useReportRequestIssueOptions(loanId, type, true);
  const data = options.data;

  const existing = data
    ? requestForPeriod(requests, type, data.suggestedPeriodStart, data.suggestedPeriodEnd)
    : null;

  // Server-enforced. Rendered VERBATIM — the endpoint's own words, so the
  // reason on screen and the reason in the 400 can never be two explanations.
  const serverBlocked = data ? !data.canIssue : false;

  // Client-side context, NOT a block. Each says what issuing anyway would mean.
  const dscrOffWatch = type === "DscrWatchMonthly" && !watch.isOnDscrWatch;
  const workoutNoCase = type === "DefaultWorkout" && !hasOpenDefaultCase;

  return (
    <Card data-testid={`report-request-type-header-${type}`}>
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-space-2">
            <h3 className="text-body font-semibold text-onyx m-0">
              {REPORT_REQUEST_TYPE_LABEL[type]}
            </h3>
            {type === "DscrWatchMonthly" && (
              <StatusBadge tone={watch.isOnDscrWatch ? "warn" : "muted"}>
                {watch.isOnDscrWatch ? "On watch" : "Not on watch"}
              </StatusBadge>
            )}
          </div>

          {options.isLoading && <Skeleton className="mt-space-2 h-5 w-64" />}

          {options.isError && (
            <p className="text-caption text-status-error m-0 mt-space-1" role="alert">
              Could not work out what is owed next for this type.
            </p>
          )}

          {data && (
            <p
              className="text-caption text-gray-mid m-0 mt-space-1"
              data-testid={`type-next-period-${type}`}
            >
              <CalendarClock size={14} aria-hidden className="inline mr-space-1 align-[-2px]" />
              Next period <strong className="text-onyx">{data.suggestedPeriodLabel}</strong>
              {data.suggestedDueDate ? (
                <>
                  {" "}
                  · due{" "}
                  <strong className="text-onyx">
                    {formatCalendarDate(data.suggestedDueDate)}
                  </strong>
                </>
              ) : (
                // No due date is the SHAPE of the Annual block, not a separate
                // gap: the deadline is computed from the tax date, so without
                // one there is no date to state.
                <> · no due date can be computed yet</>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-space-2 shrink-0">
          {/* Already covered — the door leads to the request that covers it,
              never to a second one for the same period. */}
          {existing ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onOpenRequest(existing)}
              data-testid={`type-open-existing-${type}`}
            >
              {existing.periodLabel} already issued — open it
            </Button>
          ) : (
            canReview && (
              <Button
                variant="primary"
                size="sm"
                disabled={!data || serverBlocked}
                onClick={() => onIssue(type)}
                data-testid={`type-issue-${type}`}
              >
                Issue {data?.suggestedPeriodLabel ?? REPORT_REQUEST_TYPE_LABEL[type]}
              </Button>
            )
          )}
        </div>
      </div>

      {/* The server's refusal, with the control that clears it. */}
      {serverBlocked && data && (
        <div
          className="mt-space-3 flex flex-wrap items-start justify-between gap-space-3 rounded-md border border-status-warning-ink bg-status-warning/15 px-space-3 py-space-2"
          data-testid={`type-blocked-${type}`}
          role="alert"
        >
          <div className="flex items-start gap-space-2 min-w-0">
            <AlertTriangle
              size={16}
              aria-hidden
              className="mt-[2px] shrink-0 text-status-warning-ink"
            />
            <p className="text-small text-status-warning-ink m-0">
              {data.blockedReason ??
                "This request type cannot be issued for this loan right now."}
            </p>
          </div>
          {/* Every refusal that a viewer can actually clear carries its own
              door, per the Table Standard: a workflow state someone could act
              on is not a dead end. Each is gated on the permission that door
              needs, which is NOT the same as the one that issues the request —
              E4 can put a loan on watch and cannot issue against it. */}
          {type === "Annual" && canReview && (
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={onRecordTaxDate}
              data-testid="type-blocked-door-Annual"
            >
              Record the date
            </Button>
          )}
          {type === "DscrWatchMonthly" && canToggleWatch && (
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={onPutOnWatch}
              data-testid="type-blocked-door-DscrWatchMonthly"
            >
              Put on watch
            </Button>
          )}
        </div>
      )}

      {/* Preconditions the server does NOT enforce: stated, with the door, and
          the action stays live. */}
      {dscrOffWatch && !serverBlocked && (
        <div
          className="mt-space-3 flex flex-wrap items-start justify-between gap-space-3 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
          data-testid="type-precondition-DscrWatchMonthly"
        >
          <p className="text-small text-gray-mid m-0 min-w-0">
            This loan is not on DSCR-Watch, so no month is owed and nothing here
            will tell you when one is missed. A monthly request can still be
            issued as a one-off.
          </p>
          {canToggleWatch && (
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={onPutOnWatch}
              data-testid="type-precondition-door-DscrWatchMonthly"
            >
              Put on watch
            </Button>
          )}
        </div>
      )}

      {workoutNoCase && !serverBlocked && (
        <div
          className="mt-space-3 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
          data-testid="type-precondition-DefaultWorkout"
        >
          <p className="text-small text-gray-mid m-0">
            This loan has no open default case, so a Default / Workout request
            issued now is linked to nothing and will not appear on any case
            timeline. It is still collected and reported here.
          </p>
        </div>
      )}

      {/* The DSCR-Watch missed-month fact, on the type it belongs to. It is not
          inferable from an absence — a month nobody got round to looks exactly
          like a month nobody has reached yet — which is why the server sends
          it rather than leaving it to be noticed. */}
      {type === "DscrWatchMonthly" && watch.isOnDscrWatch && (
        <div
          className={
            watch.currentMonthIssued
              ? "mt-space-3 rounded-md border border-status-success bg-status-success/10 px-space-3 py-space-2"
              : "mt-space-3 rounded-md border border-status-warning-ink bg-status-warning/15 px-space-3 py-space-2"
          }
          data-testid="dscr-watch-current-month"
        >
          <p
            className={
              watch.currentMonthIssued
                ? "text-small m-0 text-status-success"
                : "text-small m-0 text-status-warning-ink"
            }
          >
            <strong>{watch.currentMonthLabel}</strong>
            {watch.currentMonthIssued ? " request issued." : " request not yet issued."}
          </p>
        </div>
      )}
    </Card>
  );
}
